import { Decision } from '../../types/audit-trail';

// Cryptographic utilities for decision integrity and tamper detection
class DecisionCrypto {
  
  /**
   * Generate a unique device identifier
   */
  async generateDeviceId(): Promise<string> {
    // Try to get a persistent device ID
    let deviceId = localStorage.getItem('deviceId');
    
    if (!deviceId) {
      // Generate new device ID using available browser fingerprinting
      const fingerprint = await this.generateFingerprint();
      deviceId = await this.hashString(fingerprint);
      localStorage.setItem('deviceId', deviceId);
    }
    
    return deviceId;
  }

  /**
   * Generate browser fingerprint for device identification
   */
  private async generateFingerprint(): Promise<string> {
    const components = [
      navigator.userAgent,
      navigator.language,
      navigator.platform,
      window.screen.width + 'x' + window.screen.height,
      new Date().getTimezoneOffset().toString(),
      navigator.hardwareConcurrency?.toString() || '0',
      navigator.maxTouchPoints?.toString() || '0'
    ];

    // Add canvas fingerprint if available
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.textBaseline = 'top';
        ctx.font = '14px Arial';
        ctx.fillText('CLEAR Audit Trail', 2, 2);
        components.push(canvas.toDataURL());
      }
    } catch (e) {
      // Canvas fingerprinting may be blocked
    }

    return components.join('|');
  }

  /**
   * Generate SHA-256 hash of a string
   */
  async hashString(input: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Generate decision signature for tamper detection
   */
  async generateDecisionSignature(decision: Omit<Decision, 'signature'>): Promise<string> {
    // Create deterministic string representation of decision
    const signatureData = {
      id: decision.id,
      createdAt: decision.createdAt.toISOString(),
      createdBy: decision.createdBy,
      title: decision.title,
      type: decision.type,
      decisionMade: decision.decisionMade,
      rationale: decision.rationale,
      riskLevel: decision.riskLevel,
      informationSources: [...decision.informationSources].sort(),
      stakeholders: [...decision.stakeholders].sort(),
      alternatives: decision.alternatives || '',
      location: decision.location ? `${decision.location.lat},${decision.location.lng}` : '',
      deviceId: decision.deviceId,
      evidenceIds: decision.evidence.map(e => e.id).sort()
    };

    const signatureString = JSON.stringify(signatureData, Object.keys(signatureData).sort());
    return await this.hashString(signatureString);
  }

  /**
   * Verify decision signature to detect tampering
   */
  async verifyDecisionSignature(decision: Decision): Promise<boolean> {
    const expectedSignature = await this.generateDecisionSignature(decision);
    return decision.signature === expectedSignature;
  }

  /**
   * Generate hash for evidence file
   */
  async generateEvidenceHash(file: File | ArrayBuffer): Promise<string> {
    let buffer: ArrayBuffer;
    
    if (file instanceof File) {
      buffer = await file.arrayBuffer();
    } else {
      buffer = file;
    }
    
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Generate timestamp proof for decision
   */
  generateTimestampProof(decision: Decision): string {
    const timestamp = decision.createdAt.toISOString();
    const data = `${decision.id}|${timestamp}|${decision.deviceId}`;
    return btoa(data); // Base64 encode for simple timestamp proof
  }

  /**
   * Verify timestamp proof
   */
  verifyTimestampProof(decision: Decision, proof: string): boolean {
    try {
      const decoded = atob(proof);
      const [id, timestamp, deviceId] = decoded.split('|');
      
      return (
        id === decision.id &&
        timestamp === decision.createdAt.toISOString() &&
        deviceId === decision.deviceId
      );
    } catch (e) {
      return false;
    }
  }

  /**
   * Generate witness signature for co-signing
   */
  async generateWitnessSignature(
    decision: Decision, 
    witnessId: string, 
    witnessName: string
  ): Promise<string> {
    const witnessData = {
      decisionId: decision.id,
      decisionSignature: decision.signature,
      witnessId,
      witnessName,
      witnessedAt: new Date().toISOString(),
      deviceId: await this.generateDeviceId()
    };

    const witnessString = JSON.stringify(witnessData, Object.keys(witnessData).sort());
    return await this.hashString(witnessString);
  }

  /**
   * Encrypt sensitive decision data
   */
  async encryptSensitiveData(data: string, password: string): Promise<string> {
    try {
      const encoder = new TextEncoder();
      const decoder = new TextDecoder();
      
      // Generate key from password
      const passwordKey = await crypto.subtle.importKey(
        'raw',
        encoder.encode(password),
        { name: 'PBKDF2' },
        false,
        ['deriveKey']
      );

      // Generate salt
      const salt = crypto.getRandomValues(new Uint8Array(16));
      
      // Derive encryption key
      const key = await crypto.subtle.deriveKey(
        {
          name: 'PBKDF2',
          salt,
          iterations: 100000,
          hash: 'SHA-256'
        },
        passwordKey,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt']
      );

      // Generate IV
      const iv = crypto.getRandomValues(new Uint8Array(12));
      
      // Encrypt data
      const encryptedData = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        key,
        encoder.encode(data)
      );

      // Combine salt, iv, and encrypted data
      const combined = new Uint8Array(salt.length + iv.length + encryptedData.byteLength);
      combined.set(salt, 0);
      combined.set(iv, salt.length);
      combined.set(new Uint8Array(encryptedData), salt.length + iv.length);

      // Return base64 encoded result
      return btoa(String.fromCharCode(...Array.from(combined)));
    } catch (error) {
      console.error('Encryption failed:', error);
      throw new Error('Failed to encrypt data');
    }
  }

  /**
   * Decrypt sensitive decision data
   */
  async decryptSensitiveData(encryptedData: string, password: string): Promise<string> {
    try {
      const encoder = new TextEncoder();
      const decoder = new TextDecoder();
      
      // Decode base64
      const combined = new Uint8Array(
        atob(encryptedData).split('').map(char => char.charCodeAt(0))
      );

      // Extract components
      const salt = combined.slice(0, 16);
      const iv = combined.slice(16, 28);
      const encrypted = combined.slice(28);

      // Generate key from password
      const passwordKey = await crypto.subtle.importKey(
        'raw',
        encoder.encode(password),
        { name: 'PBKDF2' },
        false,
        ['deriveKey']
      );

      // Derive decryption key
      const key = await crypto.subtle.deriveKey(
        {
          name: 'PBKDF2',
          salt,
          iterations: 100000,
          hash: 'SHA-256'
        },
        passwordKey,
        { name: 'AES-GCM', length: 256 },
        false,
        ['decrypt']
      );

      // Decrypt data
      const decryptedData = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        key,
        encrypted
      );

      return decoder.decode(decryptedData);
    } catch (error) {
      console.error('Decryption failed:', error);
      throw new Error('Failed to decrypt data - invalid password or corrupted data');
    }
  }

  /**
   * Generate audit log entry hash
   */
  async generateAuditLogHash(
    action: string, 
    userId: string, 
    decisionId: string, 
    timestamp: Date, 
    details?: any
  ): Promise<string> {
    const auditData = {
      action,
      userId,
      decisionId,
      timestamp: timestamp.toISOString(),
      details: details ? JSON.stringify(details) : '',
      deviceId: await this.generateDeviceId()
    };

    const auditString = JSON.stringify(auditData, Object.keys(auditData).sort());
    return await this.hashString(auditString);
  }

  /**
   * Generate secure random ID
   */
  generateSecureId(): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Validate decision integrity (comprehensive check)
   */
  async validateDecisionIntegrity(decision: Decision): Promise<{
    isValid: boolean;
    issues: string[];
  }> {
    const issues: string[] = [];

    // Check signature
    const signatureValid = await this.verifyDecisionSignature(decision);
    if (!signatureValid) {
      issues.push('Decision signature is invalid - possible tampering detected');
    }

    // Check required fields
    if (!decision.id || !decision.title || !decision.decisionMade || !decision.rationale) {
      issues.push('Missing required decision fields');
    }

    // Check timestamp reasonableness
    const now = new Date();
    const created = new Date(decision.createdAt);
    if (created > now) {
      issues.push('Decision creation date is in the future');
    }
    if (created < new Date('2020-01-01')) {
      issues.push('Decision creation date is suspiciously old');
    }

    // Check evidence consistency
    for (const evidence of decision.evidence) {
      if (!evidence.id || !evidence.filename || !evidence.type) {
        issues.push(`Invalid evidence entry: ${evidence.filename || 'unknown'}`);
      }
    }

    // Check for required rationale length
    if (decision.rationale.length < 10) {
      issues.push('Decision rationale is too short for proper documentation');
    }

    return {
      isValid: issues.length === 0,
      issues
    };
  }
}

// Create singleton instance
export const decisionCrypto = new DecisionCrypto();
export default DecisionCrypto;